import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import ResponsiveTableWrapper from '../components/ResponsiveTableWrapper';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assessmentsService } from '../api/assessmentsService';

const slugify = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const AssessmentRoles: React.FC = () => {
  const queryClient = useQueryClient();
  const rolesQuery = useQuery({
    queryKey: ['admin', 'assessmentRoles'],
    queryFn: () => assessmentsService.listRoleDefinitions(),
  });
  const guidesQuery = useQuery({
    queryKey: ['admin', 'interviewGuides'],
    queryFn: () => assessmentsService.listInterviewGuides(),
  });

  const [displayName, setDisplayName] = useState('');
  const [roleType, setRoleType] = useState<'assessor' | 'support'>('assessor');
  const [isInterviewArea, setIsInterviewArea] = useState(false);
  const [interviewAudience, setInterviewAudience] = useState<'couple' | 'candidate_only' | 'spouse_only'>('couple');
  const [sortOrder, setSortOrder] = useState(0);

  const canCreate = useMemo(() => displayName.trim().length > 0, [displayName]);

  const guideByRoleId = useMemo(() => {
    const map = new Map<number, { id: number; questionCount?: number }>();
    for (const guide of guidesQuery.data?.data || []) {
      if (guide.roleId) map.set(guide.roleId, { id: guide.id, questionCount: guide.questionCount });
    }
    return map;
  }, [guidesQuery.data]);

  const createMutation = useMutation({
    mutationFn: async () =>
      assessmentsService.createRoleDefinition({
        name: slugify(displayName),
        displayName: displayName.trim(),
        roleType,
        isInterviewArea,
        interviewAudience,
        sortOrder,
      }),
    onSuccess: async () => {
      setDisplayName('');
      setRoleType('assessor');
      setIsInterviewArea(false);
      setInterviewAudience('couple');
      setSortOrder(0);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'assessmentRoles'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (vars: { roleId: number; payload: any }) => assessmentsService.updateRoleDefinition(vars.roleId, vars.payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'assessmentRoles'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (roleId: number) => assessmentsService.deleteRoleDefinition(roleId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'assessmentRoles'] });
    },
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
          <h1 className="text-2xl font-bold text-gray-900">Assessment Roles</h1>
          <p className="text-gray-600 mt-2">
            Manage the global role catalog (assessor vs support). Each assessment selects which roles it uses and assigns people.
          </p>
        </div>

        <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Create Role</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <input
              className="border border-gray-300 rounded-md px-3 py-2"
              placeholder="Role display name (e.g. Communication)"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <select
              className="border border-gray-300 rounded-md px-3 py-2"
              value={roleType}
              onChange={(e) => setRoleType(e.target.value as any)}
            >
              <option value="assessor">Assessor</option>
              <option value="support">Support</option>
            </select>
            <select
              className="border border-gray-300 rounded-md px-3 py-2"
              value={interviewAudience}
              onChange={(e) => setInterviewAudience(e.target.value as any)}
              disabled={roleType !== 'assessor'}
              title={roleType !== 'assessor' ? 'Only assessor roles have interview audience' : undefined}
            >
              <option value="couple">Couple</option>
              <option value="candidate_only">Candidate Only</option>
              <option value="spouse_only">Spouse Only</option>
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={isInterviewArea} onChange={(e) => setIsInterviewArea(e.target.checked)} />
              Interview Area (matrix)
            </label>
            <input
              className="border border-gray-300 rounded-md px-3 py-2"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value || '0', 10))}
              placeholder="Sort"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              disabled={!canCreate || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              Create
            </button>
            {createMutation.isError && <div className="text-sm text-red-700">{(createMutation.error as Error).message}</div>}
          </div>
        </div>

        <div className="bg-white shadow-lg rounded-xl overflow-hidden">
          <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">All Roles</h2>
          </div>
          <ResponsiveTableWrapper>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Display</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Audience</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Interview Area</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Interview Guide</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sort</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(rolesQuery.data?.data || []).map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                        defaultValue={r.displayName}
                        onBlur={(e) => updateMutation.mutate({ roleId: r.id, payload: { displayName: e.target.value } })}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <select
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                        value={r.roleType}
                        onChange={(e) => updateMutation.mutate({ roleId: r.id, payload: { roleType: e.target.value } })}
                      >
                        <option value="assessor">assessor</option>
                        <option value="support">support</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                        value={(r as any).interviewAudience || 'couple'}
                        disabled={r.roleType !== 'assessor'}
                        onChange={(e) => updateMutation.mutate({ roleId: r.id, payload: { interviewAudience: e.target.value } })}
                      >
                        <option value="couple">couple</option>
                        <option value="candidate_only">candidate_only</option>
                        <option value="spouse_only">spouse_only</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={!!r.isInterviewArea}
                        onChange={(e) => updateMutation.mutate({ roleId: r.id, payload: { isInterviewArea: e.target.checked } })}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {!r.isInterviewArea ? (
                        <span className="text-gray-400">—</span>
                      ) : guideByRoleId.has(r.id) ? (
                        <Link
                          to={`/interview-guides/${guideByRoleId.get(r.id)!.id}`}
                          className="text-primary-700 hover:text-primary-800 underline"
                        >
                          {guideByRoleId.get(r.id)!.questionCount ?? 0} questions
                        </Link>
                      ) : (
                        <Link to="/interview-guides" className="text-gray-500 hover:text-gray-700 underline">
                          None
                        </Link>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        className="w-24 border border-gray-300 rounded-md px-2 py-1 text-sm"
                        type="number"
                        defaultValue={r.sortOrder}
                        onBlur={(e) => updateMutation.mutate({ roleId: r.id, payload: { sortOrder: parseInt(e.target.value || '0', 10) } })}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                        disabled={deleteMutation.isPending}
                        onClick={() => deleteMutation.mutate(r.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {(rolesQuery.data?.data || []).length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-sm text-gray-600" colSpan={7}>
                      No roles yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </ResponsiveTableWrapper>
        </div>
      </div>
    </Layout>
  );
};

export default AssessmentRoles;

